import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { Shield, Lock, LoaderCircle, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { confirmResetPasswordSchema, ConfirmResetPasswordValues } from "@/schemas/resetPasswordSchema";

interface ConfirmResetPasswordFormProps {
  email: string;
  onSubmit: (code: string, newPassword: string) => Promise<boolean>;
  onBack: () => void;
  loading: boolean;
}

const ConfirmResetPasswordForm: React.FC<ConfirmResetPasswordFormProps> = ({
  email,
  onSubmit,
  onBack,
  loading,
}) => {
  const form = useForm<ConfirmResetPasswordValues>({
    resolver: zodResolver(confirmResetPasswordSchema),
    defaultValues: {
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = async (data: ConfirmResetPasswordValues) => {
    const success = await onSubmit(data.code, data.newPassword);
    if (success) {
      form.reset();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="text-sm text-gray-600 text-center mb-4">
          We sent a verification code to <strong>{email}</strong>
        </div>

        <div className="flex flex-col gap-4">

       

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-900">
                Verification Code
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Shield className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Enter verification code"
                    className="pl-10 h-11"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-900">
                New Password
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    className="pl-10 h-11"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-900">
                Confirm New Password
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    className="pl-10 h-11"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
         </div>

         <div className="flex flex-col gap-2">

        
        <Button
          disabled={loading}
          type="submit"
          className="w-full h-12 bg-black hover:bg-gray-800"
        >
          {loading ? (
            <>
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              <span>Resetting Password...</span>
            </>
          ) : (
            "Reset Password"
          )}
        </Button>

        <div className="flex gap-2">
          <Button
            variant="outline"
            type="button"
            className="flex-1 h-12"
            onClick={onBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Button
            variant="outline"
            type="button"
            className="flex-1 h-12"
            asChild
          >
            <Link to="/login">
              Back to Login
            </Link>
          </Button>
          </div>

        </div>
      </form>
    </Form>
  );
};

export default ConfirmResetPasswordForm;